import { useCallback } from 'react';
import { List } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Text, TextField, useFormField } from '@frontend/design-system';

type Props = {
  actionText: string;
  cancelTrackingId: string;
  list?: List;
  onCancel: () => void;
  onSave: ((name: string) => Promise<void>) | ((name: string) => void);
  saveTrackingId: string;
};

export const InteractiveListItem = ({
  actionText,
  cancelTrackingId,
  list,
  onCancel,
  onSave,
  saveTrackingId,
}: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const textFieldProps = useFormField({ type: 'text', value: list?.name ?? '' }, [list?.name]);
  const isValidValue = !!(textFieldProps.value.trim() && textFieldProps.value !== list?.name);

  const handleSave = useCallback(() => {
    if (!isValidValue) return;
    onSave(textFieldProps.value);
  }, [isValidValue, onSave, textFieldProps.value]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleSave();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      }
    },
    [handleSave, onCancel]
  );

  return (
    <div
      css={{
        border: `1px solid ${theme.colors.primary50}`,
        borderRadius: theme.borderRadius.medium,
        marginBottom: theme.spacing(1),
        padding: theme.spacing(2),
      }}
    >
      <Text size='large' weight='bold' css={{ marginBottom: theme.spacing(2) }}>
        {actionText}
      </Text>
      <div>
        <TextField
          {...textFieldProps}
          autoFocus
          css={{ marginBottom: theme.spacing(1) }}
          label={t('List Name')}
          name='list-name'
          onKeyDown={handleKeyDown}
        />
        <Button
          css={{ marginRight: theme.spacing(1) }}
          onClick={onCancel}
          size='small'
          trackingId={cancelTrackingId}
          variant='secondary'
        >
          {t('Cancel')}
        </Button>
        <Button disabled={!isValidValue} onClick={handleSave} size='small' trackingId={saveTrackingId}>
          {t('Save')}
        </Button>
      </div>
    </div>
  );
};
